import React from 'react';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { Box, Button, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { HasPermissionContainer } from '@pec/aion-ui-components/containers/HasPermission';
import { InfoDialog } from '../../components/InfoDialog';
import { OperationType } from '../../types/enums';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';
import { Trans, useTranslation } from 'react-i18next';
import { updateLead } from '../../helpers/events';
import { useHistory } from 'react-router-dom';
import { useTypedSelector } from '../../combineReducers';
import { PaymentStatus } from '@pec/aion-ui-core/interfaces/subscription';

const upgradeSessionKeys = [
  'survey:formState_organizationId_Upgrade',
  'survey:orderPageSelectedProduct_organizationId_Upgrade'
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    navLink: {
      color: 'var(--primary-blue-six)',
      fontWeight: theme.typography.fontWeightMedium,
      textDecoration: 'none'
    }
  })
);

export const UpgradeTile: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();

  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const { organization } = useTypedSelector(state => state.organization);
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);
  const { organizationInfo } = useTypedSelector(state => state.organizationInfo);
  const { Write } = ActivityAction;
  const { Subscriptions } = ActivityResourceName;
  const isFoundation = mainSubscription.description === SubscriptionTiers.Foundation;
  const showUpgradeButton = (mainSubscription?.isUpgradeAvailable || isFoundation) && !organizationInfo?.isChild;
  const isPaymentPending = mainSubscription.paymentStatus === PaymentStatus.Pending;

  const handleClick = () => {
    if (organizationInfo?.isParent) {
      const progressPayload = {
        organizationInfo,
        userInfo: null,
        mainSubscription,
        products: {
          productCodes: [],
          productIds: []
        },
        operationType: OperationType.Upgrade,
        stepName: 'UpgradeStart'
      };

      setOpenDialog(true);
      updateLead(progressPayload);
      return;
    }

    if (organization) {
      upgradeSessionKeys.forEach(key => {
        sessionStorage.removeItem(key.replace('organizationId', organization?.id));
      });
      history.push(`/${organization.id}/upgrade`);
    }
  };

  return (
    <HasPermissionContainer>
      {({ hasOrganizationPermission, hasGlobalPermission }) => (
        <>
          {showUpgradeButton && (
            <Box css={{ margin: '10px' }}>
              {organization?.id && (
                <Tooltip
                  title={
                    t('subscriber:upgrade.tooltipTitle', "Contact your company's administrator to upgrade") as string
                  }
                  disableHoverListener={
                    hasOrganizationPermission(Write, Subscriptions) || hasGlobalPermission(Write, Subscriptions)
                  }
                >
                  <span>
                    <Button
                      color="primary"
                      variant="outlined"
                      disabled={
                        !(
                          hasOrganizationPermission(Write, Subscriptions) || hasGlobalPermission(Write, Subscriptions)
                        ) || isPaymentPending
                      }
                      onClick={handleClick}
                      data-testid="upgradeButton"
                    >
                      {t('subscriber:upgrade.buttonTitle', 'Upgrade Membership')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}
          <InfoDialog
            open={openDialog}
            strings={{
              title: t('subscriber:upgrade.dialogTitle', 'Upgrade Membership'),
              confirm: t('subscriber:common.close', 'Close'),
              content: (
                <Trans key="subscriber:upgrade.dialogContent">
                  Show your commitment to creating a safer, risk-free and more compliant world for all of your employees
                  by upgrading your membership. <br />
                  <br />
                  To upgrade your membership, please contact a member of our award winning team on{' '}
                  <a href="tel:03455219222" className={classes.navLink}>
                    345 521 9222
                  </a>{' '}
                  who will be happy to help.
                </Trans>
              )
            }}
            onConfirm={() => setOpenDialog(false)}
          />
        </>
      )}
    </HasPermissionContainer>
  );
};
